import { Point } from '@ritabygglov/core';
import { useCallback, useMemo } from 'react';
import {
  findMatchesAtPoint,
  snapToElementsOnEdgeDrag,
  snapToElementsOnMove,
} from '../../../helpers/guidelines';
import { getSnapMetrics } from '../../../helpers/snapScale';
import {
  closestAlignedPoint,
  getAlignedNextPoint,
  snapPointToAngle,
} from '../../../helpers/utility';
import { AppState, Coordinate, LineSegment } from '../../../types';
import {
  EdgeSnappingContext,
  SnappingContext,
  SnappingResult,
} from '../types/interactionTypes';

export const useSnapping = (
  setSnapLines: (lines: LineSegment[]) => void,
  setSnapPoints: (points: Coordinate[]) => void,
  isShiftPressed: boolean,
  isMetaPressed: boolean,
  scale: number
) => {
  /**
   * Snap distances are screen-relative, so they are resolved from the current zoom
   * level here and passed into the geometry, which only ever sees user units.
   */
  const metrics = useMemo(() => getSnapMetrics(scale), [scale]);

  const snapToElements = useCallback(
    (context: SnappingContext): SnappingResult => {
      const { x, y, element, elements, excludeSelf = false } = context;
      if (isMetaPressed)
        return { dx: x, dy: y, matchedLines: [], matchedPoints: [] };
      return snapToElementsOnMove(
        metrics,
        x,
        y,
        elements,
        excludeSelf,
        element
      );
    },
    [isMetaPressed, metrics]
  );

  const snapEdgeToElements = useCallback(
    (context: EdgeSnappingContext): SnappingResult => {
      const { deltaX, deltaY, freeAxis, edgePoints, elements, elementId } =
        context;
      if (isMetaPressed)
        return { dx: deltaX, dy: deltaY, matchedLines: [], matchedPoints: [] };
      return snapToElementsOnEdgeDrag(
        metrics,
        deltaX,
        deltaY,
        freeAxis,
        edgePoints,
        elements,
        elementId
      );
    },
    [isMetaPressed, metrics]
  );

  const matchesAtPoint = useCallback(
    (
      point: Coordinate,
      elements: AppState['elements'],
      excludeElementId?: string
    ) => findMatchesAtPoint(metrics, point, elements, excludeElementId),
    [metrics]
  );

  const snapPolylinePoint = useCallback(
    (
      points: Point[],
      currentPoint: Point,
      nextPointIndex: number
    ): { point: Point; shouldHideSnap: boolean } => {
      let snappedPoint = { ...currentPoint };
      let shouldHideSnap = false;

      if (points.length === 2 && isShiftPressed) {
        const prevPoint = points[nextPointIndex - 1];
        const snapped = snapPointToAngle(prevPoint, currentPoint);
        snappedPoint = { ...snappedPoint, ...snapped };
        shouldHideSnap = true;
      } else if (points.length > 2 && isShiftPressed) {
        const p1 = points[nextPointIndex - 2];
        const p2 = points[nextPointIndex - 1];

        const suggestedPoint =
          getAlignedNextPoint(points, currentPoint, points.length - 2) ||
          closestAlignedPoint(p1, p2, currentPoint);

        if (suggestedPoint) {
          snappedPoint = { ...snappedPoint, ...suggestedPoint };
        }
        shouldHideSnap = true;
      }

      return { point: snappedPoint, shouldHideSnap };
    },
    [isShiftPressed]
  );

  const applySnapping = useCallback(
    (snapResult: SnappingResult, hideSnap = false) => {
      if (hideSnap) {
        setSnapLines([]);
        setSnapPoints([]);
      } else {
        setSnapLines(snapResult.matchedLines);
        setSnapPoints(snapResult.matchedPoints);
      }
    },
    [setSnapLines, setSnapPoints]
  );

  const clearSnapping = useCallback(() => {
    setSnapLines([]);
    setSnapPoints([]);
  }, [setSnapLines, setSnapPoints]);

  return {
    snapToElements,
    snapEdgeToElements,
    matchesAtPoint,
    snapPolylinePoint,
    applySnapping,
    clearSnapping,
  };
};
